/**
 * FIXTURE approval data (Story #5351 W1-T11, Screen Contract G05).
 *
 * There is no approvals backend in services/api.ts today — grep proof:
 * `grep -rn "approve\|approval" src/services/api.ts` finds no `/approvals`
 * route of any kind. Everything in this file is illustrative demo content
 * (the mockup's own "Illustrative data" pill), never a live read, and is
 * used ONLY by ApprovalsPanel's fixture mode. A receipt built from this file
 * is stamped `source: 'FIXTURE'` and is held in the panel's local React
 * state only — it is never sent to a server, never written to
 * localStorage/IndexedDB, and never survives a remount. The FIXTURE marker
 * on every receipt is deliberate: a real approvals backend (W5/W6) will one
 * day emit receipts through the same EvidenceReceipt shape, and nothing
 * downstream must ever be able to mistake one of these for that.
 */
import type { EvidenceReceipt } from '../../contracts/receipt';
import type { VerifierResult } from '../../contracts/verifier';

/** Stamped onto every receipt this file can produce — never present on a real receipt. */
export const FIXTURE_SOURCE = 'FIXTURE' as const;

export type ApprovalDecisionState = 'pending' | 'expiring' | 'expired' | 'invalidated' | 'approved';

export interface FixtureApproval {
  approvalId: string;
  requester: string;
  target: string;
  riskTier: 'low' | 'medium' | 'high';
  /** The plan hash this approval was requested against. */
  requestedPlanHash: string;
  /**
   * The plan hash as it stands right now. Differs from requestedPlanHash on
   * one fixture row on purpose — it is what makes 'invalidated' a real,
   * testable derivation (a hash comparison) rather than a hardcoded label.
   */
  currentPlanHash: string;
  expiresAt: string; // ISO 8601
  beforeSummary: string;
  afterSummary: string;
  payloadDigest: string;
}

const now = () => Date.now();
const minutesFromNow = (m: number) => new Date(now() + m * 60_000).toISOString();
const minutesAgo = (m: number) => new Date(now() - m * 60_000).toISOString();

/** Three fixture rows mirroring the mockup's own APR-311 / APR-308 / APR-302 examples. */
export const FIXTURE_APPROVALS: FixtureApproval[] = [
  {
    approvalId: 'APR-311',
    requester: 'release-operator',
    target: 'portal-x / PROD / 1234 / ap-southeast-2',
    riskTier: 'high',
    requestedPlanHash: 'ph-apr311-a1b2c3',
    currentPlanHash: 'ph-apr311-a1b2c3', // unchanged since request
    expiresAt: minutesFromNow(45),
    beforeSummary: 'portal-x release channel: stable @ v14.2.0',
    afterSummary: 'portal-x release channel: candidate @ v14.3.0 (3 services, 1 migration)',
    payloadDigest: 'sha256:1a2b3c4d…(fixture)',
  },
  {
    approvalId: 'APR-308',
    requester: 'environment-operator',
    target: 'env-uat / 5678 / ap-southeast-2',
    riskTier: 'high',
    requestedPlanHash: 'ph-apr308-d4e5f6',
    currentPlanHash: 'ph-apr308-d4e5f6',
    expiresAt: minutesFromNow(4), // deliberately near expiry
    beforeSummary: 'env-uat capacity: 2x t3.medium',
    afterSummary: 'env-uat capacity: 4x t3.medium (scale-up)',
    payloadDigest: 'sha256:2b3c4d5e…(fixture)',
  },
  {
    approvalId: 'APR-302',
    requester: 'security-operator',
    target: 'subject self-service excluded',
    riskTier: 'medium',
    requestedPlanHash: 'ph-apr302-original',
    currentPlanHash: 'ph-apr302-changed', // plan moved after the approval was requested
    expiresAt: minutesAgo(2), // also expired, to prove invalidated wins over expired in the UI copy
    beforeSummary: 'RBAC role security-reviewer: 4 members',
    afterSummary: 'RBAC role security-reviewer: 5 members (+1)',
    payloadDigest: 'sha256:3c4d5e6f…(fixture)',
  },
];

export function deriveApprovalState(approval: FixtureApproval, at: number = now()): ApprovalDecisionState {
  if (approval.currentPlanHash !== approval.requestedPlanHash) return 'invalidated';
  if (new Date(approval.expiresAt).getTime() <= at) return 'expired';
  if (new Date(approval.expiresAt).getTime() - at <= 5 * 60_000) return 'expiring';
  return 'pending';
}

/**
 * Builds the FIXTURE machine-verification step an approve must follow (the
 * single-operator shape's rule 3: "taken after independent machine
 * verification"). Deterministic and local-only — no network, no timers.
 */
export function runFixtureVerification(approval: FixtureApproval): VerifierResult {
  return {
    commandId: `fixture-cmd-${approval.approvalId}`,
    target: { environmentId: 'fixture', accountId: 'fixture', region: 'ap-southeast-2' },
    outcome: 'verified',
    observedValue: approval.afterSummary,
    expectedValue: approval.afterSummary,
    checkedAt: new Date().toISOString(),
  };
}

/**
 * Builds a FIXTURE EvidenceReceipt for a single-operator approve (DECISION
 * #5284: requester === approver; a separate deliberate step; bound to the
 * exact plan hash; taken after machine verification). The caller (
 * ApprovalsPanel) is responsible for keeping this in component state only —
 * this function performs no I/O of any kind.
 */
export function buildFixtureReceipt(
  approval: FixtureApproval,
  actor: string,
  verification: VerifierResult,
): EvidenceReceipt & { source: typeof FIXTURE_SOURCE } {
  const approvedAt = new Date().toISOString();
  return {
    source: FIXTURE_SOURCE,
    receiptId: `FIXTURE-receipt-${approval.approvalId}-${Date.now()}`,
    actor,
    target: approval.target,
    planHash: approval.currentPlanHash,
    approval: {
      approvedBy: actor, // requester === approver under DECISION #5284
      approvedAt,
      isSingleOperator: true,
    },
    commandId: `FIXTURE-cmd-${approval.approvalId}`,
    verification,
    buildHash: 'FIXTURE-build',
    result: 'success',
    redactions: [],
    retentionUntil: minutesFromNow(60 * 24), // fixture receipts are not actually retained anywhere
  };
}
